/**
 * Auth context + hook.
 *
 * Provides:
 *  - user: CurrentUser | null
 *  - isLoading: boolean  (true while /api/me is in-flight on app start)
 *  - login(email, password): Promise<void>  — calls POST /api/login, then refetches /me
 *  - logout(): Promise<void>                — calls POST /api/logout, clears query cache
 *
 * Auth guard pattern:
 *   - isLoading === true  → show full-page spinner
 *   - user === null       → redirect to /login
 *   - user !== null       → render protected content
 *
 * Mock mode: in DEV we skip the real /api/me call and return a fake user so the
 * app works without a running backend. Set VITE_USE_MOCK explicitly ('true'/'false')
 * to override the DEV default — e.g. VITE_USE_MOCK=false forces the real API in dev.
 * In production builds (import.meta.env.DEV === false, no VITE_USE_MOCK) auth is always real.
 */

import { createContext, useContext } from 'react'
import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { authApi } from '@/api/client'
import type { CurrentUser } from '@/types'

// ---- Mock helper ----

const USE_MOCK = import.meta.env.VITE_USE_MOCK != null
  ? import.meta.env.VITE_USE_MOCK === 'true'
  : import.meta.env.DEV

const MOCK_USER: CurrentUser = {
  id: 'user-rahu-001',
  email: 'rahu@touchbrand.ru',
  role: 'operator',
  display_name: 'Раху',
  is_active: true,
}

// ---- Query key ----

export const ME_QUERY_KEY = ['auth', 'me'] as const

// ---- Context types ----

export interface AuthContextValue {
  user: CurrentUser | null
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
}

export const AuthContext = createContext<AuthContextValue | null>(null)

// ---- useAuth hook ----

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}

// ---- Internal hooks used by AuthProvider ----

/** Fetches /api/me. Returns null on 401 (unauthenticated), throws on other errors. */
export function useMeQuery() {
  return useQuery<CurrentUser | null>({
    queryKey: ME_QUERY_KEY,
    queryFn: async () => {
      if (USE_MOCK) return MOCK_USER
      try {
        return await authApi.me()
      } catch (err: unknown) {
        const e = err as Error & { status?: number }
        if (e.status === 401) return null
        throw err
      }
    },
    // Never treat as stale — only refetch on explicit invalidation or window focus
    staleTime: Infinity,
    retry: false,
  })
}

export function useLoginMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      await authApi.login(email, password)
    },
    onSuccess: async () => {
      // Refetch /me so the cached user is set immediately after login
      await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY })
    },
  })
}

export function useLogoutMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      if (!USE_MOCK) await authApi.logout()
    },
    onSuccess: () => {
      // Clear all cached data on logout
      queryClient.setQueryData(ME_QUERY_KEY, null)
      queryClient.clear()
    },
  })
}
